/**
 * Две формы выбора, общие для сита ленты и сита коридоров.
 *
 * Общие намеренно, а не по бережливости: два экрана с одинаковой механикой и
 * разной внешностью человек читает как два разных продукта, а под секундомером
 * ещё и промахивается — рука уже знает, где стоял переключатель.
 *
 * Разница между ними — механика, и она обязана быть видна не наведя курсор:
 *
 *   `Seg`      — выбор ОДНОГО из немногих (лайв или прематч, «только эти»,
 *                «все, кроме» или «главная»). Утопленная дорожка, выбранное залито;
 *   `ChipList` — выбор ЛЮБОГО числа (дисциплины, конторы, рынки). Отдельные
 *                кнопки, каждая сама по себе.
 *
 * Одинаковый вид у складывающегося и у взаимоисключающего — прямой способ
 * получить нажатие, которого человек не ждал.
 */

export interface Option {
  code: string;
  name: string;
  count: number;
}

/** Выбор одного из немногих. */
export function Seg<T extends string>({
  value,
  options,
  onPick,
  label,
}: {
  value: T;
  options: readonly { code: T; name: string; count?: number }[];
  onPick: (code: T) => void;
  label: string;
}): React.ReactElement {
  return (
    <div className="nx-pick" role="group" aria-label={label}>
      {options.map((one) => (
        <button
          key={one.code}
          type="button"
          className={one.code === value ? 'on' : ''}
          aria-pressed={one.code === value}
          onClick={() => onPick(one.code)}
        >
          {one.name}
          {one.count !== undefined && one.count > 0 && <i>{one.count}</i>}
        </button>
      ))}
    </div>
  );
}

/** Выбор любого числа. Ноль строк сейчас гасит фишку, но не запрещает её. */
export function ChipList({
  options,
  chosen,
  onToggle,
}: {
  options: readonly Option[];
  chosen: readonly string[];
  onToggle: (code: string) => void;
}): React.ReactElement {
  return (
    <div className="nx-sieve__chips">
      {options.map((one) => {
        const picked = chosen.includes(one.code);
        // Выбранная не гаснет никогда, иначе свой набор в списке не найти.
        const dim = one.count === 0 && !picked;
        return (
          <button
            key={one.code}
            type="button"
            className={`nx-chip ${picked ? 'on' : ''} ${dim ? 'zero' : ''}`}
            aria-pressed={picked}
            title={one.count === 0 ? `${one.name}: сейчас в ленте нет, но выбрать можно` : ''}
            onClick={() => onToggle(one.code)}
          >
            {one.name}
            {one.count > 0 && <i>{one.count}</i>}
          </button>
        );
      })}
    </div>
  );
}
